import { cloneDeep } from 'lodash';
import { type ITextContent, type ITextLayerProps } from '@/version-types';

export class LayerDataProcessor {
    private constructor() {
        throw new Error('LayerDataPostProcessor is a static class');
    }

    /**
     * 当 textContents 有且仅有 1 个时，移除 textContents
     * 并将唯一的 textContent 合并到一级属性中
     * @param layerData - 文本图层数据
     */
    private static useOnlyOneTextContent(layerData: ITextLayerProps) {
        const textContent = layerData.textContents![0];

        const { color, fontSize, fontFamily, bold, italic, lineThrough, underline, textMark } = textContent;
        const nextLayerData: ITextLayerProps = {
            ...layerData,
            color,
            fontSize,
            fontFamily,
            bold,
            italic,
            lineThrough,
            underline,
        };

        if (!layerData.textMark && textMark) {
            Reflect.set(nextLayerData, 'textMark', textMark);
        }

        Reflect.deleteProperty(nextLayerData, 'textContents');

        return nextLayerData;
    }

    private static isSame(a: ITextContent, b: ITextContent) {
        return JSON.stringify({ ...a, text: '' }) === JSON.stringify({ ...b, text: '' });
    }

    private static merge(a: ITextContent, b: ITextContent) {
        const nextText = a.text + b.text;
        const merged: ITextContent = { ...a, text: nextText };
        return merged;
    }

    /**
     * 合并相邻的 textContent
     * @param textContents - 文本内容，此时 textContents 中长度必然大于 1
     * @returns 合并后的文本内容
     */
    private static mergeAdjacentTextContent(textContents: ITextContent[]) {
        if (textContents.length <= 1) return textContents;

        const nextTextContents: ITextContent[] = [textContents[0]];
        let cur = 1;

        while (cur < textContents.length) {
            const prevItem = nextTextContents[nextTextContents.length - 1];
            const curItem = textContents[cur];
            const isSame = this.isSame(prevItem, curItem);

            if (isSame) {
                const merged = this.merge(prevItem, curItem);
                nextTextContents[nextTextContents.length - 1] = merged;
            } else {
                nextTextContents.push(curItem);
            }

            cur += 1;
        }

        return nextTextContents;
    }

    /**
     * 移除 textContents 中的空文本
     * @param textContents - 文本内容
     */
    private static ensureHasNoEmptyTextContent(textContents: ITextContent[]) {
        return textContents.filter((item) => item.text !== '');
    }

    /**
     * 移除 textContents 中，text 不在 layer.data.text 中的文本
     * 即保证 textContents 所有文本合并后与 layer.data.text 完全一致
     * 由于通常因为 web 端输入和选区等关系，通常会造成 textContents 末尾（最后一项）出现额外的数据
     * @param textContents - 文本内容
     * @param text - 文本
     */
    private static ensureHasNoInvalidTextContent(textContents: ITextContent[], text: string) {
        if (textContents.length === 0) return textContents;

        const nextTextContents: ITextContent[] = cloneDeep(textContents);
        const combinedText = nextTextContents.map((item) => item.text).join('');

        if (combinedText === text) return nextTextContents;

        // 若 combinedText 长度小于 text 长度，则将缺少的文本添加到 textContents 最后（但是理论上不会发生这种情况）
        // 若 combinedText 长度大于 text 长度，则将多余的文本移除（更有可能发生这种情况）

        const start = nextTextContents
            .slice(0, nextTextContents.length - 1)
            .map((item) => item.text)
            .join('').length; // 除最后一项外，所有文本的长度
        const end = text.length;

        const sliceText = text.slice(start, end);
        const newItem: ITextContent = { ...nextTextContents[nextTextContents.length - 1], text: sliceText };

        nextTextContents[nextTextContents.length - 1] = newItem;
        return nextTextContents;
    }

    /**
     * 图层数据后处理
     * @param layerData - 图层数据
     */
    public static postProcess(layerData: ITextLayerProps) {
        const { textContents = [], text, advancedTextMaterial } = layerData;
        if (advancedTextMaterial) return layerData;

        let nextTextContents: ITextContent[] = [];
        nextTextContents = this.ensureHasNoEmptyTextContent(textContents);
        nextTextContents = this.ensureHasNoInvalidTextContent(nextTextContents, text);

        if (nextTextContents.length === 0) return layerData;

        if (nextTextContents.length === 1) {
            return this.useOnlyOneTextContent(layerData);
        }

        nextTextContents = this.mergeAdjacentTextContent(nextTextContents);
        if (nextTextContents.length === 1) {
            return this.useOnlyOneTextContent(layerData);
        }

        const nextLayerData: ITextLayerProps = {
            ...layerData,
            textContents: nextTextContents,
        };
        return nextLayerData;
    }
}
